import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { 
  LayoutDashboard, Users, Calendar, MessageSquare, FileImage, 
  DollarSign, BarChart3, LogOut, Menu, X, ChevronRight, Bell, Smile,
  PanelLeftClose, PanelLeftOpen, Shield
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import NotificationBell from "./NotificationBell";
import { useSidebar } from "../hooks/useSidebar";
import { useClinicSettings } from "../hooks/useClinicSettings";
import { useReceptionAlert } from "../hooks/useReceptionAlert";
import SettingsModal from "./SettingsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { collapsed, toggle } = useSidebar();
  const [showSettings, setShowSettings] = useState(false);
  const { settings: clinicSettings, fetchSettings } = useClinicSettings();
  const { patientsCount } = useReceptionAlert();

  useEffect(() => {
    if (user) fetchSettings();
  }, [user, fetchSettings]);

  // Filtrar itens do menu baseado nas permissões do usuário
  const menuItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: "Painel", path: "/dashboard", requirePermission: "access_dashboard" },
    { icon: <Users className="w-5 h-5" />, label: "Pacientes", path: "/patients" },
    { icon: <Calendar className="w-5 h-5" />, label: "Agendamentos", path: "/appointments" },
    { icon: <Smile className="w-5 h-5" />, label: "Odontograma", path: "/odontogram" },
    { icon: <MessageSquare className="w-5 h-5" />, label: "Chat IA", path: "/chat" },
    { icon: <FileImage className="w-5 h-5" />, label: "Análise de Imagens", path: "/image-analysis" },
    { icon: <Bell className="w-5 h-5" />, label: "Lembretes", path: "/reminders" },
    { icon: <DollarSign className="w-5 h-5" />, label: "Financeiro", path: "/finances" },
    { icon: <BarChart3 className="w-5 h-5" />, label: "Relatórios", path: "/reports", requirePermission: "access_reports" },
    { icon: <Shield className="w-5 h-5" />, label: "Auditoria", path: "/audit" },
  ].filter(item => {
    // Se o item requer permissão, verificar se o usuário tem
    if (item.requirePermission) {
      const userPermissions = user?.permissions || { access_dashboard: true, view_financial_cards: true, access_reports: true };
      return userPermissions[item.requirePermission] !== false;
    }
    return true; // Itens sem permissão obrigatória sempre aparecem
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-white shadow-md border border-slate-200"
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-slate-900 border-r border-slate-800 z-40 transform transition-all duration-300 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "w-[72px]" : "w-64"}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`border-b border-slate-800 ${collapsed ? "p-3" : "p-6"}`}>
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                {clinicSettings.clinic_logo ? (
                  <img
                    src={`${BACKEND_URL}${clinicSettings.clinic_logo}`}
                    alt={clinicSettings.clinic_name}
                    className="w-9 h-9 rounded-lg object-contain flex-shrink-0"
                    data-testid="sidebar-clinic-logo"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">{(clinicSettings.clinic_name || "L").charAt(0)}</span>
                  </div>
                )}
                {!collapsed && <span className="font-bold text-xl text-white truncate" data-testid="sidebar-clinic-name">{clinicSettings.clinic_name || "ItapeviDents"}</span>}
              </Link>
              {!collapsed && <NotificationBell />}
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 space-y-1 overflow-y-auto ${collapsed ? "p-2" : "p-4"}`}>
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center rounded-xl transition-all relative group/item ${
                  isActive(item.path)
                    ? "bg-teal-600 text-white font-medium"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                } ${collapsed ? "justify-center p-3" : "gap-3 px-4 py-3"}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <div className="relative">
                  {item.icon}
                  {/* Badge for Appointments with patients in reception */}
                  {item.path === "/appointments" && patientsCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                      {patientsCount}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.path === "/appointments" && patientsCount > 0 && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                        {patientsCount}
                      </span>
                    )}
                    {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </>
                )}
                {/* Tooltip when collapsed */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-slate-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover/item:opacity-100 pointer-events-none transition-opacity z-50">
                    {item.label}
                    {item.path === "/appointments" && patientsCount > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 bg-red-500 rounded-full text-[10px] font-bold">
                        {patientsCount}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Collapse Toggle */}
          <div className={`border-t border-slate-800 ${collapsed ? "p-2" : "px-4 py-2"}`}>
            <button
              onClick={toggle}
              className={`flex items-center rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-all w-full ${
                collapsed ? "justify-center p-3" : "gap-3 px-4 py-2.5"
              }`}
              data-testid="sidebar-toggle"
            >
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
              {!collapsed && <span className="text-sm">Esconder menu</span>}
            </button>
          </div>

          {/* User Section */}
          <div className={`border-t border-slate-800 ${collapsed ? "p-2" : "p-4"}`}>
            {collapsed ? (
              <>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex justify-center w-full mb-2 group/user"
                  data-testid="user-avatar-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full ring-2 ring-transparent group-hover/user:ring-teal-400 transition-all" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center ring-2 ring-transparent group-hover/user:ring-teal-400 transition-all">
                      <span className="font-medium text-white text-sm">{user?.name?.charAt(0) || "U"}</span>
                    </div>
                  )}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center w-full p-3 rounded-xl text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-all group/logout relative"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="absolute left-full ml-2 px-2 py-1 bg-slate-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover/logout:opacity-100 pointer-events-none transition-opacity z-50">
                    Sair
                  </span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-3 w-full mb-4 p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors text-left"
                  data-testid="user-info-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center">
                      <span className="font-medium text-white">{user?.name?.charAt(0) || "U"}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{user?.name || "Usuário"}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  </div>
                </button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-slate-300 border-slate-700 hover:text-red-400 hover:border-red-800 hover:bg-slate-800"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </Button>
              </>
            )}
          </div>
        </div>
      </aside>

      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </>
  );
}
